/**
 * Enrollment API 모듈
 *
 * 수강 정보 관련 API 요청 함수들을 제공합니다.
 * - 모든 함수는 apiClient를 통해 인증 토큰이 자동으로 포함됩니다.
 * - 에러 발생 시 호출부에서 처리할 수 있도록 throw합니다.
 */

import type {
  GetEnrollmentsParams,
  GetEnrollmentsResponse,
  EnrollmentDetailDto,
  CreateSpecialProductEnrollmentsRequest,
  GetTargetCourseAndAccountIdsQuery,
  GetTargetCourseAndAccountIdsResponse,
  CreateEnrollmentsByRequest,
} from '@repo/types';
import type { EnrollmentState } from '@theailab-dev/parchment';
import apiClient from '../lib/apiClient';

/**
 * 수강 상세 정보 조회
 *
 * 흐름:
 * 1. enrollmentId를 URL 파라미터로 사용
 * 2. GET /enrollment/{enrollmentId} 요청
 * 3. 수강 상세 정보 반환 (수강 정보 + 코스 정보 + 계정 정보)
 *
 * @param enrollmentId - 조회할 수강 ID
 * @returns EnrollmentDetailDto - packages/types에 정의된 수강 상세 정보
 *   - 수강 정보: id, state, kind, enrolledAt, completedAt 등
 *   - 진도 정보: totalLessonCount, completedLessonCount, lastAttendedAt 등
 *   - 코스 정보: course (id, title, state, kind, level 등)
 *   - 계정 정보: account (id, name, email)
 * @throws API 요청 실패 시 에러 (404: 수강 정보 없음, 401: 인증 실패 등)
 */
export const getEnrollmentDetail = async (enrollmentId: number): Promise<EnrollmentDetailDto> => {
  try {
    console.log('🔍 수강 상세 조회 요청:', enrollmentId);

    // API 요청: GET /enrollment/{enrollmentId}
    // packages/types의 EnrollmentDetailDto 타입 사용으로 타입 안전성 보장
    const response = await apiClient.get<EnrollmentDetailDto>(`/enrollment/${enrollmentId}`);

    console.log('✅ 수강 상세 조회 성공:', response.data);
    return response.data;
  } catch (error) {
    // 에러 로깅 후 호출부로 전달 (react-query에서 처리)
    console.error('❌ 수강 상세 조회 실패:', error);
    throw error;
  }
};

/**
 * 수강 상태 변경
 *
 * 엔드포인트: PATCH /api/enrollment/:id/state
 * - 관리자 권한 필요
 * - COMPLETED가 아닌 상태에서 다른 상태로 변경 가능
 * - 가능한 상태: IN_PROGRESS, COMPLETED, CREDITED, SUSPENDED, CANCELED, EXPIRED
 *
 * 흐름:
 * 1. enrollmentId와 새 상태(state)를 body에 포함 { state: EnrollmentState }
 * 2. PATCH /enrollment/{enrollmentId}/state 요청
 * 3. 서버에서 수강 상태 변경 (COMPLETED 상태는 변경 불가)
 * 4. 업데이트된 EnrollmentDetailDto 반환
 *
 * @param enrollmentId - 수강 ID
 * @param state - 변경할 상태 (IN_PROGRESS, CREDITED, SUSPENDED, CANCELED, EXPIRED 등)
 * @returns 업데이트된 수강 상세 정보
 * @throws API 요청 실패 시 에러 (400: COMPLETED 상태 변경 불가, 404: 수강 없음 등)
 */
export const changeEnrollmentState = async (
  enrollmentId: number,
  state: EnrollmentState,
): Promise<EnrollmentDetailDto> => {
  try {
    console.log('🔄 수강 상태 변경 요청:', { enrollmentId, state });

    // API 요청: PATCH /enrollment/{enrollmentId}/state
    const response = await apiClient.patch<EnrollmentDetailDto>(`/enrollment/${enrollmentId}/state`, { state });

    console.log('✅ 수강 상태 변경 성공:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ 수강 상태 변경 실패:', error);
    throw error;
  }
};

/**
 * 수강권 전체 목록 조회 (페이지네이션)
 *
 * 엔드포인트: GET /api/enrollments
 */
export const getEnrollments = async (params: GetEnrollmentsParams): Promise<GetEnrollmentsResponse> => {
  try {
    console.log('🔍 수강권 목록 조회 요청 params:', params);
    const response = await apiClient.get<GetEnrollmentsResponse>(`/enrollments`, { params });
    console.log('✅ 수강권 목록 조회 응답:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ 수강권 목록 조회 실패:', error);
    throw error;
  }
};

// 단체 유료특강 수강권 생성
export const grantSpecialProductEnrollments = async (
  payload: CreateSpecialProductEnrollmentsRequest,
): Promise<void> => {
  try {
    console.log('🔍 수강권 부여 요청:', payload);
    await apiClient.post(`/enrollments/special-product`, payload);
    console.log('✅ 수강권 부여 성공');
  } catch (error) {
    console.error('❌ 수강권 부여 실패:', error);
    throw error;
  }
};

// 개별 유료특강 수강권 생성
export const grantEnrollments = async (payload: CreateEnrollmentsByRequest): Promise<void> => {
  try {
    console.log('🔍 수강권 부여 요청:', payload);
    await apiClient.post(`/enrollments`, payload);
    console.log('✅ 수강권 부여 성공');
  } catch (error) {
    console.error('❌ 수강권 부여 실패:', error);
    throw error;
  }
};

//todo: type, endpoint 맞추기
//유료특강 수강권 부여 가능 여부 조회
export const getTargetCourseAndAccountIds = async (
  params: GetTargetCourseAndAccountIdsQuery,
): Promise<GetTargetCourseAndAccountIdsResponse[]> => {
  try {
    console.log('🔍 수강권 부여 가능 여부 조회 요청:', params);
    const response = await apiClient.get(`/enrollments/target-accounts`, { params });
    console.log('✅ 수강권 부여 가능 여부 조회 성공:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ 수강권 부여 가능 여부 조회 실패:', error);
    throw error;
  }
};
